import { Injectable } from '@angular/core';
import * as Msal from '@azure/msal-browser';
import { from, switchMap } from 'rxjs';
import { AuthService } from '../api/common/auth.service';
import { ExternalDomainConfig, SessionApiService } from '../api/session-api.service';
import { NotificationService } from '../pages/notification.service';
import { SpinnerService } from '../pages/spinner.service';

@Injectable({
  providedIn: 'root',
})
export class MicrosoftService {
  private msalInstance!: Msal.PublicClientApplication;
  private config: ExternalDomainConfig | undefined;
  private clientId: string | undefined;
  private scopes: string[] = [];

  public constructor(
    private authService: AuthService,
    private sessionApiService: SessionApiService,
    private spinnerService: SpinnerService,
    private notificationService: NotificationService,
  ) {}

  public init(config: ExternalDomainConfig, username: string): void {
    this.config = config;
    this.clientId = config.clientId;
    this.scopes = config.scopes.split(';');

    if (this.msalInstance) {
      console.warn('Msal already initialized');
      this.acquireTokenSilent(username);
    } else {
      this.msalInstance = new Msal.PublicClientApplication(MicrosoftService.getConfiguration(this.clientId));
      from(this.msalInstance.initialize())
        .pipe(switchMap(() => from(this.msalInstance.handleRedirectPromise())))
        .subscribe({
          next: response => {
            this.redirectHandler(response, username);
          },
          error: error => {
            console.error(error);
            if (error instanceof Msal.InteractionRequiredAuthError && error.errorCode === Msal.InteractionRequiredAuthErrorCodes.consentRequired) {
              this.authService.logOut(true);
            } else if (error.errorCode === 'invalid_client') {
              this.scopes = [];
              this.acquireTokenSilent(username);
            } else {
              this.notificationService.error('bkwa.errors.generic.oneLine', true);
            }
            this.spinnerService.hide();
          },
        });
    }
  }

  private redirectLogin(email: string): void {
    if (!this.msalInstance) {
      throw new Error('Instance not initialized');
    }
    this.msalInstance.loginRedirect({ scopes: this.scopes, loginHint: email });
  }

  public logOut(email: string): Promise<void> {
    if (!this.msalInstance) {
      throw new Error('Instance not initialized');
    }
    this.spinnerService.show();
    return this.msalInstance.logoutRedirect({
      logoutHint: email,
    });
  }

  private redirectHandler(resp: Msal.AuthenticationResult | null, username: string): void {
    if (resp != null) {
      this.accessTokenHandler(resp);
    } else {
      this.acquireTokenSilent(username);
    }
  }

  private acquireTokenSilent(username: string): void {
    if (!this.msalInstance) {
      throw new Error('Instance not initialized');
    }

    const currentRoute = window.location.hash;
    if (currentRoute) {
      this.authService.saveRedirectAfterLogin(currentRoute.substring(1));
    }
    this.spinnerService.show({ timeout: 15000 });

    const account = this.msalInstance.getAccount({ loginHint: username });
    if (account) {
      const accessTokenRequest: Msal.SilentRequest = {
        scopes: this.scopes,
        account,
        cacheLookupPolicy: Msal.CacheLookupPolicy.AccessTokenAndRefreshToken,
      };
      this.msalInstance.acquireTokenSilent(accessTokenRequest).then(
        accessTokenResponse => {
          this.accessTokenHandler(accessTokenResponse);
        },
        () => {
          this.redirectLogin(username);
        },
      );
    } else {
      this.redirectLogin(username);
    }
  }

  private accessTokenHandler(authentication: Msal.AuthenticationResult): void {
    if (authentication.accessToken === null) {
      throw new Error('Access token is null');
    }
    this.sessionApiService.loginExternal(authentication.accessToken, 'MICROSOFT').then(() => {
      if (!this.config) {
        throw new Error('Microsoft config is undefined');
      }
      this.authService.logInExternal(
        'MICROSOFT',
        this.config,
        authentication.account?.username || '',
        authentication.accessToken,
        authentication.expiresOn,
      );
      window.setTimeout(() => {
        this.spinnerService.hide();
      }, 300);
    });
  }

  private static getConfiguration(clientId: string): Msal.Configuration {
    const redirectUri = window.location.origin + '/';
    return {
      auth: {
        clientId,
        redirectUri,
        postLogoutRedirectUri: redirectUri,
      },

      cache: {
        cacheLocation: Msal.BrowserCacheLocation.LocalStorage,
      },
    };
  }
}
